export interface PlaceAutocompleteRequest {
  /**
   * The text string on which to search. The Place Autocomplete service will return candidate matches
   * based on this string and order results based on their perceived relevance.
   */
  input: string;
  /**
   * A random string which identifies an autocomplete
   * [session](https://developers.google.com/places/web-service/autocomplete#session_tokens) for billing purposes.
   * If this parameter is omitted from an autocomplete request, the request is billed independently
   */
  sessiontoken?: string;
  /**
   * The position, in the input term, of the last character that the service uses to match predictions.
   * For example, if the input is 'Google' and the `offset` is 3, the service will match on 'Goo'.
   * The string determined by the `offset` is matched against the first word in the input term only.
   * For example, if the input term is 'Google abc' and the offset is 3, the service will attempt to match against 'Goo abc'.
   * If no `offset` is supplied, the service will use the whole term.
   * The `offset` should generally be set to the position of the text caret.
   */
  offset?: number;
  /**
   * The origin point from which to calculate straight-line distance to the destination (returned as distance_meters).
   * If this value is omitted, straight-line distance will not be returned.
   */
  origin?: LatLng;
  /** The point around which you wish to retrieve place information. */
  location?: LatLng;
  /**
   * The distance (in meters) within which to return place results. Note that setting a radius biases results to the indicated area,
   * but may not fully restrict results to the specified area.
   */
  radius?: number;
  /**
   * The language code, indicating in which language the results should be returned, if possible.
   * Searches are also biased to the selected language; results in the selected language may be given a higher ranking.
   * See the list of supported languages and their codes.
   * Note that we often update supported languages so this list may not be exhaustive.
   * If language is not supplied, the Place Autocomplete service will attempt to use the native language
   * of the domain from which the request is sent.
   */
  language?: string;
  /** The types of place results to return. */
  types?: PlaceAutocompleteType;
  /**
   * A grouping of places to which you would like to restrict your results.
   * Currently, you can use `components` to filter by up to 5 countries.
   * Countries must be passed as a two character, ISO 3166-1 Alpha-2 compatible country code.
   * For example: `components=country:fr` would restrict your results to places within France.
   * Multiple countries must be passed as multiple `country:XX` filters, with the pipe character (`|`) as a separator.
   * For example: `components=country:us|country:pr|country:vi|country:gu|country:mp` would restrict your results
   * to places within the United States and its unincorporated organized territories.
   */
  components?: string[];
  /**
   * Returns only those places that are strictly within the region defined by `location` and `radius`.
   * This is a restriction, rather than a bias, meaning that results outside this region
   * will not be returned even if they match the user input.
   */
  strictbounds?: boolean;
}

export enum PlaceAutocompleteType {
  /**
   * instructs the Place Autocomplete service to return only geocoding results, rather than business results.
   * Generally, you use this request to disambiguate results where the location specified may be indeterminate.
   */
  geocode = 'geocode',
  /**
   * instructs the Place Autocomplete service to return only geocoding results with a precise address.
   * Generally, you use this request when you know the user will be looking for a fully specified address.
   */
  address = 'address',
  /** instructs the Place Autocomplete service to return only business results. */
  establishment = 'establishment',
  /**
   * the `(regions)` type collection instructs the Places service to return any result matching the following types:
   *  - `locality`
   *  - `sublocality`
   *  - `postal_code`
   *  - `country`
   *  - `administrative_area_level_1`
   *  - `administrative_area_level_2`
   */
  regions = '(regions)',
  /** the (cities) type collection instructs the Places service to return results that match `locality` or `administrative_area_level_3`. */
  cities = '(cities)',
}

export type LatLng = LatLngArray | LatLngString | LatLngLiteral | LatLngLiteralVerbose;

export type LatLngArray = [number, number];

export type LatLngString = string;

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

export interface LatLngLiteralVerbose {
  latitude: number;
  longitude: number;
}

export enum PlaceAutocompleteLanguage {
  /** Arabic */
  ar = 'ar',
  /** Belarusian */
  be = 'be',
  /** Bulgarian */
  bg = 'bg',
  /** Bengali */
  bn = 'bn',
  /** Catalan */
  ca = 'ca',
  /** Czech */
  cs = 'cs',
  /** Danish */
  da = 'da',
  /** German */
  de = 'de',
  /** Greek */
  el = 'el',
  /** English */
  en = 'en',
  /** English (Australian) */
  en_Au = 'en-Au',
  /** English (Great Britain) */
  en_GB = 'en-GB',
  /** Spanish */
  es = 'es',
  /** Basque */
  eu = 'eu',
  /** Farsi */
  fa = 'fa',
  /** Finnish */
  fi = 'fi',
  /** Filipino */
  fil = 'fil',
  /** French */
  fr = 'fr',
  /** Galician */
  gl = 'gl',
  /** Gujarati */
  gu = 'gu',
  /** Hindi */
  hi = 'hi',
  /** Croatian */
  hr = 'hr',
  /** Hungarian */
  hu = 'hu',
  /** Indonesian */
  id = 'id',
  /** Italian */
  it = 'it',
  /** Hebrew */
  iw = 'iw',
  /** Japanese */
  ja = 'ja',
  /** Kazakh */
  kk = 'kk',
  /** Kannada */
  kn = 'kn',
  /** Korean */
  ko = 'ko',
  /** Kyrgyz */
  ky = 'ky',
  /** Lithuanian */
  lt = 'lt',
  /** Latvian */
  lv = 'lv',
  /** Macedonian */
  mk = 'mk',
  /** Malayalam */
  ml = 'ml',
  /** Marathi */
  mr = 'mr',
  /** Burmese */
  my = 'my',
  /** Dutch */
  nl = 'nl',
  /** Norwegian */
  no = 'no',
  /** Punjabi */
  pa = 'pa',
  /** Polish */
  pl = 'pl',
  /** Portuguese */
  pt = 'pt',
  /** Portuguese (Brazil) */
  pt_BR = 'pt-BR',
  /** Portuguese (Portugal) */
  pt_PT = 'pt-PT',
  /** Romanian */
  ro = 'ro',
  /** Russian */
  ru = 'ru',
  /** Slovak */
  sk = 'sk',
  /** Slovenian */
  sl = 'sl',
  /** Albanian */
  sq = 'sq',
  /** Serbian */
  sr = 'sr',
  /** Swedish */
  sv = 'sv',
  /** Tamil */
  ta = 'ta',
  /** Telugu */
  te = 'te',
  /** Thai */
  th = 'th',
  /** Tagalog */
  tl = 'tl',
  /** Turkish */
  tr = 'tr',
  /** Ukrainian */
  uk = 'uk',
  /** Uzbek */
  uz = 'uz',
  /** Vietnamese */
  vi = 'vi',
  /** Chinese (Simlified) */
  zh_CN = 'zh-CN',
  /** Chinese (Traditional) */
  zh_TW = 'zh-TW',
}
